// PDF / CSV export helpers for the Sell Through Pictorial report.
// Everything here is stateless: field definitions, per-product value getters,
// the html2canvas clone preparation, and the jsPDF cover-page header.
import { format } from "date-fns";
import {
	getProductPeriodStPct,
	getProductRealisation,
	formatRos,
	formatGPPercentage,
	formatGPValue,
} from "./pictorialUtils";
import { getSizeRank } from "./sizeUtils";

/** Tile metrics the user can include or omit from PDF/CSV export (clone-only for PDF). */
export const PICTORIAL_EXPORT_FIELDS = [
	{ id: "summary_header", label: "Summary header (date range, Buy/Sold totals)" },
	{ id: "image", label: "Product image (or placeholder)" },
	{ id: "option", label: "OPTION (PKEY)" },
	{ id: "mrp", label: "MRP" },
	{ id: "fit_type", label: "Fit (Denim Pants only)" },
	// KPI band on the card, in the order it renders
	{ id: "tinw", label: "Inw (T.INW)" },
	{ id: "total_sold", label: "T.Sold Qty" },
	{ id: "sold_in_period", label: "Pd.Sold Qty" },
	{ id: "online", label: "Online" },
	{ id: "offline", label: "Offline" },
	{ id: "gp_pct", label: "GP%" },
	{ id: "gp_value", label: "GP Value" },
	{ id: "gp_per_piece", label: "GP/Pcs" },
	{ id: "asp", label: "ASP" },
	{ id: "discount_pct", label: "Discount %" },
	{ id: "discount_value", label: "Discount Value" },
	{ id: "season", label: "Season" },
	{ id: "category", label: "Category" },
	{ id: "cogs", label: "COGS" },
	{ id: "multi_mrp_cogs", label: "Multi (MRP/COGS)" },
	{ id: "balance", label: "Balance" },
	{ id: "period_st", label: "Sold Period ST%" },
	{ id: "st", label: "Total ST%" },
	{ id: "soh", label: "SOH" },
	{ id: "inv_pivotal", label: "Pivotal SOH (S,M,L)" },
	{ id: "inv_non_pivotal", label: "Non-pivotal SOH" },
	{ id: "avg_aging", label: "Aging" },
	{ id: "max_recv_aging", label: "Aging Last Inw" },
	{ id: "first_inw_date", label: "First Inw Date" },
	{ id: "max_inw_date", label: "Last Inw Date" },
	{ id: "avg_doh", label: "Avg DOH" },
	{ id: "period_ros", label: "Period Wgt. ROS" },
	{ id: "stock_cover_period", label: "Stock Cover (period)" },
	{ id: "ros", label: "Wgt. ROS" },
	{ id: "blend", label: "Blend (fabric)" },
	{ id: "palette_type", label: "Palette Type" },
];

/** @deprecated alias — use PICTORIAL_EXPORT_FIELDS */
export const PICTORIAL_PDF_TILE_FIELDS = PICTORIAL_EXPORT_FIELDS;

/**
 * CSV-only column groups mirroring the on-card hover tooltips (Sale / SOH / DOH / Aging / ROS breakdowns).
 * Each entry is one checkbox in the export picker (CSV exports only) and expands to the
 * columns listed in EXPORT_CSV_GROUP_FIELD_MAP. Never rendered in the PDF.
 */
export const PICTORIAL_CSV_TOOLTIP_FIELDS = [
	{ id: "csv_channel_sold_period", label: "Channel sales breakdown — period" },
	{ id: "csv_channel_sold_overall", label: "Channel sales breakdown — overall" },
	{ id: "csv_soh_split", label: "SOH split — barcode × facility (WH / EBO)" },
	{ id: "csv_size_soh", label: "Size-wise SOH — WH / EBO (* = pivotal size)" },
	{ id: "csv_channel_doh", label: "Channel-wise DOH" },
	{ id: "csv_channel_aging", label: "Channel-wise aging" },
	{ id: "csv_channel_last_inw_aging", label: "Channel-wise aging — last inward" },
	{ id: "csv_channel_inw_dates", label: "Channel-wise first / last inward dates" },
	{ id: "csv_channel_ros", label: "Channel-wise Wgt. ROS" },
];

export const defaultExportFieldMask = () =>
	Object.fromEntries([
		...PICTORIAL_EXPORT_FIELDS.map(({ id }) => [id, id !== "summary_header"]),
		...PICTORIAL_CSV_TOOLTIP_FIELDS.map(({ id }) => [id, true]),
	]);

export const defaultPdfTileFieldMask = defaultExportFieldMask;

export const pdfExportMenuSecondary =
	"Page 1 = summary & filters only; products from page 2. Pick tile fields (OPTION–BLEND) in the next step.";

export const getProductImageUrlForExport = (product) => {
	const raw = product?.image_url ?? product?.URL;
	if (raw == null) return "";
	const s = String(raw).trim();
	if (s === "" || s === "-" || s === "N/A") return "";
	const lower = s.toLowerCase();
	if (lower === "null" || lower === "undefined") return "";
	return s;
};

export const getProductGpPerPieceForExport = (product) => {
	const gpValue = parseFloat(String(product?.lcogs?.gp_value || "0").replace(/,/g, "")) || 0;
	const unitsSold = parseFloat(product?.troco_ls?.t_sale) || 0;
	if (!unitsSold) return "—";
	return Math.round(gpValue / unitsSold).toLocaleString("en-IN");
};

export const getProductAvgDohForExport = (product) => {
	const dohValues = [
		product?.ebo_doh || 0,
		product?.lfs_doh || 0,
		product?.mbo_doh || 0,
		product?.ecom_doh || 0,
	].filter((value) => value > 0);
	if (dohValues.length === 0) return "0";
	return Math.round((dohValues.reduce((sum, value) => sum + value, 0) / dohValues.length) * 10) / 10;
};

export const EXPORT_CSV_FIELD_MAP = {
	image: { header: "Image URL", getValue: getProductImageUrlForExport },
	option: { header: "Option", getValue: (p) => p.PKEY || "N/A" },
	brand: { header: "Brand", getValue: (p) => p.brand || "N/A" },
	subBrand: { header: "Sub Brand", getValue: (p) => p.subBrand || "N/A" },
	category: { header: "Category", getValue: (p) => p.category || "N/A" },
	department: { header: "Department", getValue: (p) => p.category || "N/A" },
	subCategory: { header: "Sub Category", getValue: (p) => p.sub_category || "N/A" },
	style: { header: "Style", getValue: (p) => p.style || "N/A" },
	color: { header: "Color", getValue: (p) => p.color || "N/A" },
	season: { header: "Season", getValue: (p) => p.season || "N/A" },
	fit_type: { header: "Fit Type", getValue: (p) => p.fitType || "N/A" },
	mrp: { header: "MRP", getValue: (p) => p.troco_ls?.mrp || "N/A" },
	cogs: { header: "COGS", getValue: (p) => p.COGS || "N/A" },
	tinw: { header: "T.INW", getValue: (p) => p.troco_ls?.t_inw || "0" },
	multi_mrp_cogs: {
		header: "Multi (MRP/COGS)",
		getValue: (p) => {
			const mrp = parseFloat(String(p.troco_ls?.mrp ?? "").replace(/,/g, "")) || 0;
			const cogs = parseFloat(String(p.COGS ?? "").replace(/,/g, "")) || 0;
			if (!cogs) return "—";
			return (mrp / cogs).toFixed(2);
		},
	},
	asp: {
		header: "ASP",
		getValue: (p) => {
			const { asp } = getProductRealisation(p);
			return asp == null ? "—" : Math.round(asp);
		},
	},
	discount_pct: {
		header: "Discount %",
		getValue: (p) => {
			const { discountPct } = getProductRealisation(p);
			return discountPct == null ? "—" : `${discountPct.toFixed(2)}%`;
		},
	},
	discount_value: {
		header: "Discount Value",
		getValue: (p) => {
			const { discountValue } = getProductRealisation(p);
			return discountValue == null ? "—" : Math.round(discountValue);
		},
	},
	sold_in_period: { header: "Sold In Period", getValue: (p) => p.troco_ls?.t_sale ?? "0" },
	total_sold: { header: "Total Sold", getValue: (p) => p.troco_ls?.t_sale_total ?? "0" },
	balance: { header: "Balance", getValue: (p) => p.troco_ls?.balance ?? "0" },
	period_st: {
		header: "Sold Period ST%",
		getValue: (p) => `${getProductPeriodStPct(p).toFixed(2)}%`,
	},
	st: {
		header: "Total ST%",
		getValue: (p) => `${(p.troco_ls?.st ?? 0).toFixed(2)}%`,
	},
	gp_pct: {
		header: "GP%",
		getValue: (p) => p.lcogs?.gp_percentage || "0%",
	},
	gp_value: { header: "GP Value", getValue: (p) => p.lcogs?.gp_value || "0" },
	gp_per_piece: { header: "GP/Pc", getValue: getProductGpPerPieceForExport },
	soh: {
		header: "SOH",
		getValue: (p) => (p.total_wh_soh || 0) + (p.total_ebo_soh || 0),
	},
	inv_pivotal: {
		header: "Pivotal SOH",
		getValue: (p) => Math.round(Number(p.inv_pivotal) || 0),
	},
	inv_non_pivotal: {
		header: "Non-pivotal SOH",
		getValue: (p) => Math.round(Number(p.inv_non_pivotal) || 0),
	},
	avg_aging: {
		header: "Aging (days)",
		getValue: (p) => {
			if (p.avg_aging != null && p.avg_aging !== "" && !Number.isNaN(Number(p.avg_aging))) {
				return Math.round(Number(p.avg_aging));
			}
			const vals = [p.ebo_aging, p.lfs_aging, p.mbo_aging, p.ecom_aging].filter((v) => Number(v) > 0);
			if (vals.length === 0) return "0";
			return Math.round(vals.reduce((s, v) => s + Number(v), 0) / vals.length);
		},
	},
	max_recv_aging: {
		header: "Aging Last Inw (days)",
		getValue: (p) => {
			if (p.avg_max_aging != null && p.avg_max_aging !== "" && !Number.isNaN(Number(p.avg_max_aging))) {
				return Math.round(Number(p.avg_max_aging));
			}
			const vals = [p.ebo_max_aging, p.lfs_max_aging, p.mbo_max_aging, p.ecom_max_aging].filter((v) => Number(v) > 0);
			if (vals.length === 0) return "0";
			return Math.round(vals.reduce((s, v) => s + Number(v), 0) / vals.length);
		},
	},
	first_inw_date: {
		header: "First Inw Date",
		getValue: (p) => p.first_inw_date || "—",
	},
	max_inw_date: {
		header: "Last Inw Date",
		getValue: (p) => p.max_inw_date || "—",
	},
	avg_doh: { header: "Avg DOH (days)", getValue: getProductAvgDohForExport },
	period_ros: { header: "Period Wgt. ROS", getValue: (p) => formatRos(p.period_ros) },
	stock_cover_period: {
		header: "Stock Cover (days)",
		getValue: (p) => {
			const cover = p.stock_cover_period;
			if (cover == null || cover === 0 || Number.isNaN(Number(cover))) return "—";
			return Number(cover).toFixed(1);
		},
	},
	ros: { header: "Wgt. ROS", getValue: (p) => formatRos(p.rate_of_sale) },
	blend: { header: "Blend", getValue: (p) => p.blend || p.fabricBlends || "N/A" },
	palette_type: {
		header: "Palette Type",
		getValue: (p) => p.paletteType || p.palette || "N/A",
	},
	online: {
		header: "Online Cont%",
		getValue: (p) => `${p.lcogs?.oln_cont || "0%"} (${p.online_sold_qty || 0})`,
	},
	offline: {
		header: "Offline Cont%",
		getValue: (p) => `${p.lcogs?.offln_cont || "0%"} (${p.offline_sold_qty || 0})`,
	},
};

/** "S*:3 | M*:0 | L*:5 | XL:2" — one cell per SOH source (wh_soh / ebo_soh); * marks pivotal sizes. */
const formatSizeSohBreakdown = (product, field) => {
	const rows = Array.isArray(product?.size_soh_breakdown) ? product.size_soh_breakdown : [];
	if (rows.length === 0) return "—";
	return [...rows]
		.sort((a, b) => getSizeRank(a.size || a.size_label) - getSizeRank(b.size || b.size_label))
		.map((r) => {
			const size = r.size || r.size_label || "?";
			const pivotal = r.pivotal || r.is_pivotal ? "*" : "";
			return `${size}${pivotal}:${Math.round(Number(r[field]) || 0)}`;
		})
		.join(" | ");
};

/** Channel Wgt. ROS = period sold ÷ channel aging (same maths as the card tooltip). */
const channelRosForExport = (soldQty, aging) => {
	const a = Number(aging) || 0;
	if (a <= 0) return "—";
	return ((Number(soldQty) || 0) / a).toFixed(2);
};

const formatInwDateForExport = (value) => {
	if (value == null || String(value).trim() === "") return "—";
	return String(value);
};

/** Columns produced by each CSV-only tooltip group (PICTORIAL_CSV_TOOLTIP_FIELDS ids). */
export const EXPORT_CSV_GROUP_FIELD_MAP = {
	csv_channel_sold_period: [
		{ header: "EBO Sold (Period)", getValue: (p) => p.ebo_sold_qty || 0 },
		{ header: "LFS Sold (Period)", getValue: (p) => p.lfs_sold_qty || 0 },
		{ header: "MBO Sold (Period)", getValue: (p) => p.mbo_sold_qty || 0 },
		{ header: "D2C Sold (Period)", getValue: (p) => p.d2c_sold_qty || 0 },
		{ header: "Marketplace Sold (Period)", getValue: (p) => p.marketplace_sold_qty || 0 },
		{ header: "Other Sold (Period)", getValue: (p) => p.other_sold_qty || 0 },
	],
	csv_channel_sold_overall: [
		{ header: "EBO Sold (Overall)", getValue: (p) => p.ebo_sold_qty_total || 0 },
		{ header: "LFS Sold (Overall)", getValue: (p) => p.lfs_sold_qty_total || 0 },
		{ header: "MBO Sold (Overall)", getValue: (p) => p.mbo_sold_qty_total || 0 },
		{ header: "D2C Sold (Overall)", getValue: (p) => p.d2c_sold_qty_total || 0 },
		{ header: "Marketplace Sold (Overall)", getValue: (p) => p.marketplace_sold_qty_total || 0 },
		{ header: "Other Sold (Overall)", getValue: (p) => p.other_sold_qty_total || 0 },
	],
	csv_size_soh: [
		{ header: "Size-wise SOH WH (*=pivotal)", getValue: (p) => formatSizeSohBreakdown(p, "wh_soh") },
		{ header: "Size-wise SOH EBO (*=pivotal)", getValue: (p) => formatSizeSohBreakdown(p, "ebo_soh") },
	],
	csv_channel_doh: [
		{ header: "EBO DOH (days)", getValue: (p) => p.ebo_doh || 0 },
		{ header: "LFS DOH (days)", getValue: (p) => p.lfs_doh || 0 },
		{ header: "MBO DOH (days)", getValue: (p) => p.mbo_doh || 0 },
		{ header: "ECOM DOH (days)", getValue: (p) => p.ecom_doh || 0 },
	],
	csv_channel_aging: [
		{ header: "EBO Aging (days)", getValue: (p) => p.ebo_aging || 0 },
		{ header: "LFS Aging (days)", getValue: (p) => p.lfs_aging || 0 },
		{ header: "MBO Aging (days)", getValue: (p) => p.mbo_aging || 0 },
		{ header: "ECOM Aging (days)", getValue: (p) => p.ecom_aging || 0 },
	],
	csv_channel_last_inw_aging: [
		{ header: "EBO Aging Last Inw (days)", getValue: (p) => p.ebo_max_aging || 0 },
		{ header: "LFS Aging Last Inw (days)", getValue: (p) => p.lfs_max_aging || 0 },
		{ header: "MBO Aging Last Inw (days)", getValue: (p) => p.mbo_max_aging || 0 },
		{ header: "ECOM Aging Last Inw (days)", getValue: (p) => p.ecom_max_aging || 0 },
	],
	csv_channel_inw_dates: [
		{ header: "EBO First Inw Date", getValue: (p) => formatInwDateForExport(p.ebo_first_inw_date) },
		{ header: "LFS First Inw Date", getValue: (p) => formatInwDateForExport(p.lfs_first_inw_date) },
		{ header: "MBO First Inw Date", getValue: (p) => formatInwDateForExport(p.mbo_first_inw_date) },
		{ header: "ECOM First Inw Date", getValue: (p) => formatInwDateForExport(p.ecom_first_inw_date) },
		{ header: "EBO Last Inw Date", getValue: (p) => formatInwDateForExport(p.ebo_max_inw_date) },
		{ header: "LFS Last Inw Date", getValue: (p) => formatInwDateForExport(p.lfs_max_inw_date) },
		{ header: "MBO Last Inw Date", getValue: (p) => formatInwDateForExport(p.mbo_max_inw_date) },
		{ header: "ECOM Last Inw Date", getValue: (p) => formatInwDateForExport(p.ecom_max_inw_date) },
	],
	csv_channel_ros: [
		{ header: "EBO Wgt. ROS", getValue: (p) => channelRosForExport(p.ebo_sold_qty, p.ebo_aging) },
		{ header: "LFS Wgt. ROS", getValue: (p) => channelRosForExport(p.lfs_sold_qty, p.lfs_aging) },
		{ header: "MBO Wgt. ROS", getValue: (p) => channelRosForExport(p.mbo_sold_qty, p.mbo_aging) },
		{ header: "ECOM Wgt. ROS", getValue: (p) => channelRosForExport(p.ecom_sold_qty, p.ecom_aging) },
	],
};

export const TILE_EXPORT_FIELD_IDS = PICTORIAL_EXPORT_FIELDS.filter(({ id }) => id !== "summary_header").map(
	({ id }) => id
);

export const hasTileExportFieldsSelected = (mask) => TILE_EXPORT_FIELD_IDS.some((id) => mask[id]);

export const hasCsvExportContentSelected = (mask) =>
	hasTileExportFieldsSelected(mask) || Boolean(mask?.csv_soh_split);

/** Column headers for the barcode × facility SOH section appended to CSV exports. */
export const BARCODE_FACILITY_SOH_CSV_HEADERS = [
	"Option",
	"Barcode",
	"Brand",
	"Department",
	"Style",
	"Color",
	"Size",
	"Season",
	"Facility",
	"SOH Type",
	"SOH Qty",
];

export const barcodeFacilitySohRowToCsvCells = (row) => [
	row.pkey ?? "",
	row.barcode ?? "",
	row.brand ?? "",
	row.department ?? "",
	row.style ?? "",
	row.color ?? "",
	row.size ?? "",
	row.season ?? "",
	row.facility ?? "",
	row.soh_type ?? "",
	row.soh_qty ?? 0,
];

/** Hide unchecked `[data-pdf-field]` nodes in the cloned DOM only. */
export const applyPdfFieldMaskToClone = (clonedDoc, mask) => {
	if (!clonedDoc || !mask) return;
	clonedDoc.querySelectorAll("[data-pdf-field]").forEach((el) => {
		const id = el.getAttribute("data-pdf-field");
		if (!id) return;
		if (mask[id] === false) {
			el.style.display = "none";
		}
	});
};

/** Grid fields whose values run long enough to need the reduced PDF value font. */
const PDF_SMALL_VALUE_FIELDS = new Set(["multi_mrp_cogs"]);

/** Match ProductCard metrics in PDF clone: 5× minmax(0,1fr), small type, label ellipsis, value wrap, smaller GP */
const PDF_METRIC_LABEL_FONT_PX = 8;
const PDF_METRIC_VALUE_FONT_PX = 8;
const PDF_METRIC_GP_VALUE_FONT_PX = 7;
const PDF_METRIC_COL_GAP_PX = 4;
const PDF_METRIC_ROW_GAP_PX = 4;
const PDF_METRIC_CELL_PADDING_RIGHT_PX = 3;

/**
 * After field mask: reflow visible metric cells into an even grid (1–6 columns)
 * so PDF tiles match the live ProductCard (no horizontal scroll, compact font, aligned columns).
 */
export const reflowPdfMetricsGridsInClone = (clonedDoc) => {
	if (!clonedDoc) return;
	const metricFieldOrder = PICTORIAL_PDF_TILE_FIELDS.map(({ id }) => id).filter((id) => id !== "image" && id !== "option");

	clonedDoc.querySelectorAll("[data-pdf-metrics-grid]").forEach((grid) => {
		const cellsInOrder = metricFieldOrder
			.map((id) => grid.querySelector(`[data-pdf-field="${id}"]`))
			.filter(Boolean);
		const visible = cellsInOrder.filter((el) => el.style.display !== "none");

		while (grid.firstChild) {
			grid.removeChild(grid.firstChild);
		}

		if (visible.length === 0) {
			grid.style.display = "none";
			return;
		}

		const cols = Math.min(6, visible.length);
		grid.style.display = "grid";
		grid.style.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`;
		grid.style.maxWidth = "100%";
		grid.style.width = "100%";
		grid.style.boxSizing = "border-box";
		grid.style.columnGap = `${PDF_METRIC_COL_GAP_PX}px`;
		grid.style.rowGap = `${PDF_METRIC_ROW_GAP_PX}px`;
		grid.style.alignItems = "start";
		grid.style.justifyItems = "stretch";
		grid.style.textAlign = "left";
		grid.style.lineHeight = "1.2";

		visible.forEach((el) => {
			el.style.display = "";
			el.style.gridRow = "auto";
			const fieldId = el.getAttribute("data-pdf-field");
			if ((fieldId === "blend" || fieldId === "palette_type") && cols > 1) {
				el.style.gridColumn = "span 1";
			} else {
				el.style.removeProperty("grid-column");
			}
			el.style.removeProperty("grid-row");
			el.style.width = "100%";
			el.style.minWidth = "0";
			el.style.maxWidth = "100%";
			el.style.textAlign = "left";
			el.style.boxSizing = "border-box";
			el.style.paddingRight = `${PDF_METRIC_CELL_PADDING_RIGHT_PX}px`;
			el.style.overflowWrap = "anywhere";
			el.style.wordBreak = "break-word";

			/* Tooltip cells nest Typographies; direct children are not always the two lines */
			const typos = el.querySelectorAll(".MuiTypography-root");
			if (typos.length >= 1) {
				const labelEl = typos[0];
				labelEl.style.fontSize = `${PDF_METRIC_LABEL_FONT_PX}px`;
				labelEl.style.fontWeight = "700";
				labelEl.style.lineHeight = "1.2";
				labelEl.style.whiteSpace = "normal";
				labelEl.style.overflowWrap = "anywhere";
				labelEl.style.wordBreak = "break-word";
				labelEl.style.maxWidth = "100%";
			}
			if (typos.length >= 2) {
				const valueEl = typos[1];
				const valuePx = PDF_SMALL_VALUE_FIELDS.has(fieldId)
					? PDF_METRIC_GP_VALUE_FONT_PX
					: PDF_METRIC_VALUE_FONT_PX;
				valueEl.style.fontSize = `${valuePx}px`;
				valueEl.style.fontWeight = "400";
				valueEl.style.lineHeight = "1.2";
				valueEl.style.whiteSpace = "normal";
				valueEl.style.maxWidth = "100%";
				valueEl.style.minWidth = "0";
				valueEl.style.overflowWrap = "anywhere";
				valueEl.style.wordBreak = "break-word";
				if (PDF_SMALL_VALUE_FIELDS.has(fieldId)) {
					valueEl.style.fontVariantNumeric = "tabular-nums";
				}
			}

			grid.appendChild(el);
		});
	});
};

const PDF_KPI_LABEL_FONT_PX = 7;
const PDF_KPI_VALUE_FONT_PX = 8;

/** Fallback band column count when the live track list cannot be read. */
const PDF_KPI_FALLBACK_COLUMNS = 6;

/**
 * KPI band: its column count comes from a CSS container query, and those do not resolve reliably in
 * the html2canvas clone. Copy the track count the live card actually resolved to, so the PDF wraps
 * the band exactly the way the screen does.
 */
export const syncPdfKpiStripsToLiveLayout = (liveRoot, clonedDoc) => {
	if (!clonedDoc) return;
	const liveStrips = liveRoot ? liveRoot.querySelectorAll("[data-pdf-kpi-strip]") : [];
	const cloneStrips = clonedDoc.querySelectorAll("[data-pdf-kpi-strip]");

	cloneStrips.forEach((strip, i) => {
		const visible = Array.from(strip.querySelectorAll("[data-pdf-field]")).filter(
			(el) => el.style.display !== "none"
		);

		if (visible.length === 0) {
			strip.style.display = "none";
			return;
		}

		const live = liveStrips[i];
		const liveTracks = live ? window.getComputedStyle(live).gridTemplateColumns : "";
		const liveColumns =
			liveTracks && liveTracks !== "none" ? liveTracks.trim().split(/\s+/).length : 0;
		const cols = Math.min(liveColumns || PDF_KPI_FALLBACK_COLUMNS, visible.length);

		strip.style.display = "grid";
		strip.style.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`;
		strip.style.width = "100%";
		strip.style.boxSizing = "border-box";

		visible.forEach((el) => {
			el.style.minWidth = "0";
			el.style.maxWidth = "100%";
			el.style.textAlign = "center";
			const typos = el.querySelectorAll(".MuiTypography-root");
			if (typos.length >= 1) {
				typos[0].style.fontSize = `${PDF_KPI_LABEL_FONT_PX}px`;
				typos[0].style.fontWeight = "700";
				typos[0].style.lineHeight = "1.2";
			}
			if (typos.length >= 2) {
				typos[1].style.fontSize = `${PDF_KPI_VALUE_FONT_PX}px`;
				typos[1].style.fontWeight = "700";
				typos[1].style.lineHeight = "1.2";
				typos[1].style.fontVariantNumeric = "tabular-nums";
			}
		});
	});
};

/**
 * Match clone image stage size to the live DOM, then render packshots with contain + flex center in the clone only.
 * Avoids html2canvas stretching images horizontally; may add small letterboxing inside the tile.
 */
export const syncPdfImageStagesToLiveLayout = (liveRoot, clonedDoc) => {
	if (!liveRoot || !clonedDoc) return;
	const origStages = liveRoot.querySelectorAll("[data-pdf-image-stage]");
	const cloneStages = clonedDoc.querySelectorAll("[data-pdf-image-stage]");
	for (let i = 0; i < origStages.length; i++) {
		const o = origStages[i];
		const c = cloneStages[i];
		if (!o || !c) continue;
		const r = o.getBoundingClientRect();
		const h = Math.max(1, Math.round(r.height));
		const w = Math.max(1, Math.round(r.width));
		c.style.boxSizing = "border-box";
		c.style.height = `${h}px`;
		c.style.width = `${w}px`;
		c.style.minHeight = `${h}px`;
		c.style.maxHeight = `${h}px`;
		c.style.minWidth = `${w}px`;
		c.style.maxWidth = `${w}px`;
		c.style.overflow = "hidden";
		c.style.display = "flex";
		c.style.alignItems = "center";
		c.style.justifyContent = "center";
		const oImg = o.querySelector("img");
		const cImg = c.querySelector("img");
		if (cImg && oImg) {
			cImg.style.flexShrink = "0";
			cImg.style.width = "auto";
			cImg.style.height = "auto";
			cImg.style.maxWidth = "100%";
			cImg.style.maxHeight = "100%";
			cImg.style.objectFit = "contain";
			cImg.style.objectPosition = "center center";
		}
	}
};

export const preparePdfDomClone = (liveRoot, clonedDoc, fieldMask) => {
	syncPdfImageStagesToLiveLayout(liveRoot, clonedDoc);
	applyPdfFieldMaskToClone(clonedDoc, fieldMask);
	syncPdfKpiStripsToLiveLayout(liveRoot, clonedDoc);
	reflowPdfMetricsGridsInClone(clonedDoc);
	clonedDoc.querySelectorAll("[data-pdf-hide]").forEach((el) => { el.style.display = "none"; });
};

export const waitForImagesInElement = (root) => {
	if (!root) return Promise.resolve();
	const images = root.getElementsByTagName("img");
	if (images.length === 0) return Promise.resolve();
	return Promise.all(
		Array.from(images).map(
			(img) =>
				new Promise((resolve) => {
					if (img.complete) resolve();
					else {
						img.onload = () => resolve();
						img.onerror = () => resolve();
					}
				})
		)
	);
};

export const escapeCsvCell = (value) => {
	const str = value == null ? "" : String(value);
	return `"${str.replace(/"/g, '""')}"`;
};

// Display labels for filters printed on the PDF cover page
export const filterLabels = {
	brand: "Brand",
	subBrand: "Sub Brand",
	department: "Department",
	subCategory: "Sub Category",
	style: "Style",
	color: "Color",
	sleeve: "Sleeve",
	division: "Division",
	season: "Season",
	fashionability: "Fashionability",
	occasion: "Occasion",
	palette: "Palette",
	fitType: "Fit Type",
	// Key must match the filters-state key (fabricBlend, singular) or the Blend
	// filter silently disappears from the PDF cover's Applied Filters section.
	fabricBlend: "Fabric Blend",
	nod: "NOD",
	channel: "Channel",
	store: "Store",
	city: "City",
	state: "State",
	region: "Region",
	tier: "Tier",
};

/** Cover-page header (title banner, totals, applied filters). Returns Y (mm) just below the last line. */
export const addPdfCoverHeader = (pdf, { currentDateTime, dateRange, totals, calculateST, calculatePeriodST, formatNumber, pageWidth, filters, quickFilterLabel }) => {
	// Add black rectangle at the top
	pdf.setFillColor(0, 0, 0);
	pdf.rect(0, 0, pageWidth, 12, "F");

	// Add title in white
	pdf.setTextColor(255, 255, 255);
	pdf.setFont("helvetica", "bold");
	pdf.setFontSize(20);
	pdf.text("SELL THROUGH PICTORIAL", pageWidth / 2, 8, {
		align: "center",
		renderingMode: "fill",
	});

	// Reset text color to black for rest of the content
	pdf.setTextColor(0, 0, 0);
	pdf.setFontSize(10);
	pdf.setFont("helvetica", "normal");

	// Center-aligned header content
	const centerX = pageWidth / 2;
	const startY = 18;
	const lineHeight = 5;

	// Date and period info - center aligned
	pdf.text(`Date: ${format(currentDateTime, "dd-MM-yyyy HH:mm:ss")}`, centerX, startY, { align: "center" });
	pdf.text(
		`Period: ${format(dateRange.startDate, "dd-MM-yyyy")} - ${format(dateRange.endDate, "dd-MM-yyyy")}`,
		centerX,
		startY + lineHeight,
		{ align: "center" }
	);
	pdf.text(
		`Sub Category: ${totals.subCategory === "All" ? "All Sub Categories" : totals.subCategory}`,
		centerX,
		startY + lineHeight * 2,
		{ align: "center" }
	);

	// Summary stats - center aligned in a compact format
	const statsY = startY + lineHeight * 4;
	pdf.setFont(undefined, "bold");
	pdf.text(
		`Total Buy: ${formatNumber(totals.totalBuy)} | Sold: ${formatNumber(totals.totalSold)} | Period ST%: ${calculatePeriodST()}% | Total ST%: ${calculateST()}%`,
		centerX,
		statsY,
		{ align: "center" }
	);
	pdf.text(
		`MRP Value: ${formatNumber(totals.mrpValue)} | GP%: ${formatGPPercentage(
			totals.gpPercentage
		)}% | GP Value: ${formatGPValue(totals.gpValue)}`,
		centerX,
		statsY + lineHeight,
		{ align: "center" }
	);
	pdf.setFont(undefined, "normal");

	// Print selected filters - center aligned
	const filterEntries = Object.entries(filters)
		.filter(([key, value]) => filterLabels[key] && value && value !== "")
		.map(([key, value]) => `${filterLabels[key]}: ${value}`);

	// Applied filters — extra gap below KPI block so it reads as a separate section on the cover page
	let filterY = statsY + lineHeight * 4;
	pdf.setFont(undefined, "bold");
	pdf.text("Applied Filters:", centerX, filterY, { align: "center" });
	pdf.setFont(undefined, "normal");
	filterY += lineHeight;

	if (filterEntries.length > 0) {
		const filtersPerLine = 4;
		for (let i = 0; i < filterEntries.length; i += filtersPerLine) {
			const line = filterEntries.slice(i, i + filtersPerLine).join("    |    ");
			pdf.text(line, centerX, filterY, { align: "center" });
			filterY += lineHeight;
		}
	} else {
		pdf.text("No filters applied", centerX, filterY, { align: "center" });
		filterY += lineHeight;
	}

	if (quickFilterLabel) {
		filterY += lineHeight * 0.5;
		pdf.setFont(undefined, "bold");
		pdf.text("Stock quick filter:", centerX, filterY, { align: "center" });
		pdf.setFont(undefined, "normal");
		filterY += lineHeight;
		pdf.text(quickFilterLabel, centerX, filterY, { align: "center" });
		filterY += lineHeight;
	}

	// Y (mm) just below last line — for optional cover-page footnotes
	return filterY + 6;
};
